import {Injectable} from '@angular/core';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {Action, Store} from '@ngrx/store';

import {EMPTY, forkJoin, Observable, of} from 'rxjs';

import {GroupInvitation, OrgInvitation, RemoveGroupUser} from '../../../shared';
import {UserService} from '../../services/user.service';
import {OrgService} from '../../services/org.service';
import * as user from '../actions/user.action';
import * as device from '../../../afb-api/actions/device.actions';
import * as fromRoot from '../../../reducers';
import {OrganizationUser} from '../../../afb-api/models/organization-user';
import {catchError, concatMap, map, switchMap, tap} from 'rxjs/internal/operators';
import {OrganizationUserService} from '../../../afb-api/services/organization-user.service';

@Injectable()
export class UserEffects {

  @Effect()
  setOrgUserAccess$: Observable<Action> = this.actions$.pipe(
    ofType(user.SET_ORG_USER_ACCESS),
    tap(() => this.state$.dispatch(new user.StartSettingOrgAccessAction())),
    map((action: user.SetOrgUserAccessAction) => action.payload),
    switchMap((orgUserAccessData: {
      orgToken: string,
      inviteAdminList: string[],
      removeUserList: OrganizationUser[],
      removePendingAdminList: OrganizationUser[],
      removePendingMemberList: OrganizationUser[],
      removeAllGroupList: OrganizationUser[]
    }) => {
      const inviteObs = [];
      const removeOrgObs = [];
      const removeAllGroupObs = [];
      const removePendingAdminListObs = [];
      const removePendingMemberListObs = [];

      orgUserAccessData.inviteAdminList.map((inviteAdminEmail: string) => {
        inviteObs.push(this.userService.inviteToOrg(orgUserAccessData.orgToken, inviteAdminEmail, 'admin'));
      });
      orgUserAccessData.removeUserList.map((removeUser: OrganizationUser) => {
        removeOrgObs.push(this.userService.removeFromOrg(orgUserAccessData.orgToken, removeUser.userId));
      });
      orgUserAccessData.removeAllGroupList.map((removeUser: OrganizationUser) => {
        removeUser.groups.forEach((groupId: number) => {
          removeAllGroupObs.push(this.userService.removeFromGroup(orgUserAccessData.orgToken, groupId.toString(), removeUser.userId));
        });
      });
      orgUserAccessData.removePendingAdminList.map((removePendingAdmin: OrganizationUser) => {
        removePendingAdminListObs.push(this.organizationUserService.deletePendingOrganizationUser({
          organizationToken: orgUserAccessData.orgToken,
          email: removePendingAdmin.email
        }));
      });
      orgUserAccessData.removePendingMemberList.map((removePendingMember: OrganizationUser) => {
        removePendingAdminListObs.push(this.organizationUserService.deletePendingOrganizationMember({
          organizationToken: orgUserAccessData.orgToken,
          email: removePendingMember.email,
          groupId: removePendingMember.pendingGroups[0].toString()
        }));
      });

      const newObs = [];
      if (inviteObs.length > 0) {
        newObs.push(forkJoin(inviteObs));
      }
      if (removeOrgObs.length > 0) {
        newObs.push(forkJoin(removeOrgObs));
      }
      if (removeAllGroupObs.length > 0) {
        newObs.push(forkJoin(removeAllGroupObs));
      }
      if (removePendingAdminListObs.length > 0) {
        newObs.push(forkJoin(removePendingAdminListObs));
      }
      if (removePendingMemberListObs.length > 0) {
        newObs.push(forkJoin(removePendingMemberListObs));
      }

      return forkJoin(newObs).pipe(
        concatMap((res: any) => [
          new user.SetOrgUserAccessSuccessAction(),
          new user.RequestOrgUserListAction(orgUserAccessData.orgToken),
          // If a member/an admin gets removed from organization, there might be changes in devices
          new device.GetOrganizationDevices({organizationToken: orgUserAccessData.orgToken})
        ])
      );
    }),
    catchError(() => of(new user.SetOrgUserAccessFailAction()))
  );

  @Effect()
  setGroupUserAccess$: Observable<Action> = this.actions$.pipe(
    ofType(user.SET_GROUP_USER_ACCESS),
    tap(() => this.state$.dispatch(new user.StartSettingGroupAccessAction())),
    map((action: user.SetGroupUserAccessAction) => action.payload),
    switchMap((groupUserAccessData: {
      orgToken: string,
      inviteMemberList: string[],
      removeMemberList: OrganizationUser[],
      addMemberList: OrganizationUser[],
      groupId: string
    }) => {
      const inviteMemberObs = [];
      const removeMemberObs = [];

      groupUserAccessData.inviteMemberList.map((inviteMemberEmail: string) => {
        inviteMemberObs.push(
          this.userService.inviteToGroup(groupUserAccessData.orgToken, inviteMemberEmail, 'member', groupUserAccessData.groupId)
        );
      });
      groupUserAccessData.addMemberList.map((addMember: OrganizationUser) => {
        inviteMemberObs.push(
          this.userService.inviteToGroup(groupUserAccessData.orgToken, addMember.email, 'member', groupUserAccessData.groupId)
        );
      });
      groupUserAccessData.removeMemberList.map((removeMember: OrganizationUser) => {
        removeMemberObs.push(
          this.userService.removeFromGroup(groupUserAccessData.orgToken, groupUserAccessData.groupId, removeMember.userId)
        );
      });

      const newObs = [];
      if (inviteMemberObs.length > 0) {
        newObs.push(forkJoin(inviteMemberObs));
      }
      if (removeMemberObs.length > 0) {
        newObs.push(forkJoin(removeMemberObs));
      }

      return forkJoin(newObs).pipe(
        concatMap((res: any) => [
          new user.SetGroupUserAccessSuccessAction(),
          new user.RequestOrgUserListAction(groupUserAccessData.orgToken),
          new device.GetOrganizationDevices({organizationToken: groupUserAccessData.orgToken})
        ])
      );
    }),
    catchError(() => of(new user.SetGroupUserAccessFailAction()))
  );

  @Effect()
  requestOrgUserList$: Observable<Action> = this.actions$.pipe(
    ofType(user.REQUEST_ORG_USER_LIST),
    map((action: user.RequestOrgUserListAction) => action.payload),
    switchMap((orgToken: string) => {
      if (orgToken === '') {
        return EMPTY;
      }

      return this.orgService.requestOrgUserList(orgToken).pipe(
        map((resOrgUserList: OrganizationUser[]) => new user.RequestOrgUserListCompleteAction(resOrgUserList))
      );
    }),
    catchError(() => of(new user.RequestOrgUserListCompleteAction([])))
  );

  // Setting Access/Permission to user/s
  @Effect()
  inviteOrg$: Observable<Action> = this.actions$.pipe(
    ofType(user.INVITE_ORG),
    tap(() => this.state$.dispatch(new user.StartInvitingOrgAction())),
    map((action: user.InviteOrgAction) => action.payload),
    switchMap((payload: {
      orgToken: string,
      orgInvitationList: OrgInvitation[]
    }) => {
      const obs = [];

      payload.orgInvitationList.forEach((orgInvitation: OrgInvitation) => {
        obs.push(this.userService.inviteToOrg(payload.orgToken, orgInvitation.email, orgInvitation.role));
      });

      return forkJoin(obs).pipe(
        concatMap((res: any) => [
          new user.InviteOrgSuccessAction(),
          new user.RequestOrgUserListAction(payload.orgToken)
        ])
      );
    }),
    catchError(() => of(new user.InviteOrgFailAction()))
  );

  @Effect()
  inviteGroup$: Observable<Action> = this.actions$.pipe(
    ofType(user.INVITE_GROUP),
    tap(() => this.state$.dispatch(new user.StartInvitingGroupAction())),
    map((action: user.InviteGroupAction) => action.payload),
    switchMap((payload: {
      orgToken: string,
      groupInvitationList: GroupInvitation[]
    }) => {
      const obs = [];

      payload.groupInvitationList.forEach((groupInvitation: GroupInvitation) => {
        obs.push(this.userService.inviteToGroup(payload.orgToken, groupInvitation.email, groupInvitation.role, groupInvitation.groupId));
      });

      return forkJoin(obs).pipe(
        concatMap((res: any) => [
          new user.InviteGroupSuccessAction(),
          new user.RequestOrgUserListAction(payload.orgToken)
        ])
      );
    }),
    catchError(() => of(new user.InviteGroupFailAction()))
  );

  @Effect()
  removeFromOrg$: Observable<Action> = this.actions$.pipe(
    ofType(user.REMOVE_FROM_ORG),
    tap(() => this.state$.dispatch(new user.StartRemovingFromOrgAction())),
    map((action: user.RemoveFromOrgAction) => action.payload),
    switchMap((payload: {
      orgToken: string,
      userIdList: string[]
    }) => {
      const obs = [];

      payload.userIdList.forEach((userId: string) => {
        obs.push(this.userService.removeFromOrg(payload.orgToken, userId));
      });

      return forkJoin(obs).pipe(
        concatMap((res: any) => [
          new user.RemoveFromOrgSuccessAction(),
          new user.RequestOrgUserListAction(payload.orgToken)
        ])
      );
    }),
    catchError(() => of(new user.RemoveFromOrgFailAction()))
  );

  @Effect()
  removeFromGroup$: Observable<Action> = this.actions$.pipe(
    ofType(user.REMOVE_FROM_GROUP),
    tap(() => this.state$.dispatch(new user.StartRemovingFromGroupAction())),
    map((action: user.RemoveFromGroupAction) => action.payload),
    switchMap((payload: {
      orgToken: string,
      removeGroupUserList: RemoveGroupUser[]
    }) => {
      const obs = [];

      payload.removeGroupUserList.forEach((removeGroupUser: RemoveGroupUser) => {
        obs.push(this.userService.removeFromGroup(payload.orgToken, removeGroupUser.groupId, removeGroupUser.userId));
      });

      return forkJoin(obs).pipe(
        concatMap((res: any) => [
          new user.RemoveFromGroupSuccessAction(),
          new user.RequestOrgUserListAction(payload.orgToken)
        ])
      );
    }),
    catchError(() => of(new user.RemoveFromGroupFailAction()))
  );

  @Effect()
  removeFromAllGroup$: Observable<Action> = this.actions$.pipe(
    ofType(user.REMOVE_FROM_ALL_GROUP),
    tap(() => this.state$.dispatch(new user.StartRemovingFromGroupAction())),
    map((action: user.RemoveFromAllGroupAction) => action.payload),
    switchMap((payload: {
      orgToken: string,
      orgUserList: OrganizationUser[]
    }) => {
      const obs = [];

      payload.orgUserList.forEach((orgUser: OrganizationUser) => {
        orgUser.groups.forEach((groupId: number) => {
          obs.push(this.userService.removeFromGroup(payload.orgToken, groupId.toString(), orgUser.userId));
        });
      });

      return forkJoin(obs).pipe(
        concatMap((res: any) => [
          new user.RemoveFromAllGroupSuccessAction(),
          new user.RequestOrgUserListAction(payload.orgToken)
        ])
      );
    }),
    catchError(() => of(new user.RemoveFromAllGroupFailAction()))
  );

  constructor(private actions$: Actions,
              private state$: Store<fromRoot.State>,
              private orgService: OrgService,
              private userService: UserService,
              private organizationUserService: OrganizationUserService) {
  }
}
